import { hasCreateGroupsPermission } from "@utils/permission-utils";
import { RequiredRoleCompanyLevelName } from "@utils/access-control/company/company-access-control-types";
/**
 * Object that determines all different permission rules for users to get
 * access on the company level.
 */
export const requiredRolesCompanyLevel = {
  [RequiredRoleCompanyLevelName.canViewCompanyMemberProfile]: {
    companyRoles: ["COMPANY_EXECUTIVE", "COMPANY_VIEWER"],
    shouldAllowCurrentUser: true
  },
  [RequiredRoleCompanyLevelName.canChangeDisplayName]: {
    companyRoles: ["COMPANY_EXECUTIVE"],
    shouldAllowCurrentUser: true
  },
  [RequiredRoleCompanyLevelName.canInviteUsersToCompany]: {
    companyRoles: ["COMPANY_EXECUTIVE"]
  },
  [RequiredRoleCompanyLevelName.canResendInvitationToCompany]: {
    companyRoles: ["COMPANY_EXECUTIVE", "COMPANY_MANAGER"]
  },
  [RequiredRoleCompanyLevelName.canEditMember]: {
    companyRoles: ["COMPANY_EXECUTIVE"]
  },
  [RequiredRoleCompanyLevelName.canCreateGroups]: {
    companyRoles: ["COMPANY_EXECUTIVE"],
    companyManager: {
      shouldCreateGroups: true
    }
  },
  [RequiredRoleCompanyLevelName.canDeleteMemberFromCompany]: {
    companyRoles: ["COMPANY_EXECUTIVE"]
  },
  [RequiredRoleCompanyLevelName.canEditDefaultPlatform]: {
    companyRoles: ["COMPANY_EXECUTIVE"]
  },
  [RequiredRoleCompanyLevelName.canViewTeams]: {
    companyRoles: ["COMPANY_EXECUTIVE", "COMPANY_VIEWER"]
  },
  [RequiredRoleCompanyLevelName.canFetchTeams]: {
    companyRoles: ["COMPANY_EXECUTIVE", "COMPANY_VIEWER", "COMPANY_MANAGER", "PROJECT_MANAGER", "MEMBER"]
  },
  [RequiredRoleCompanyLevelName.canCreateNewTeam]: {
    companyRoles: ["COMPANY_EXECUTIVE"]
  },
  [RequiredRoleCompanyLevelName.canDeleteMembersFromTeam]: {
    companyRoles: ["COMPANY_EXECUTIVE"]
  },
  [RequiredRoleCompanyLevelName.canEditTeams]: {
    companyRoles: ["COMPANY_EXECUTIVE"]
  },
  [RequiredRoleCompanyLevelName.canDeleteTeams]: {
    companyRoles: ["COMPANY_EXECUTIVE"]
  },
  [RequiredRoleCompanyLevelName.canInviteMembersToTeam]: {
    companyRoles: ["COMPANY_EXECUTIVE"]
  },
  [RequiredRoleCompanyLevelName.canRemoveMemberFromProject]: {
    companyRoles: ["COMPANY_EXECUTIVE"]
  },
  [RequiredRoleCompanyLevelName.canChangeMemberProjectRole]: {
    companyRoles: ["COMPANY_EXECUTIVE"]
  },
  [RequiredRoleCompanyLevelName.canViewSubscriptionDetails]: {
    companyRoles: ["COMPANY_EXECUTIVE"]
  },
  [RequiredRoleCompanyLevelName.canViewProductDetails]: {
    companyRoles: ["COMPANY_EXECUTIVE"]
  },
  [RequiredRoleCompanyLevelName.canExportWorkspace]: {
    companyRoles: ["COMPANY_EXECUTIVE"]
  },
  [RequiredRoleCompanyLevelName.canEditAllCompanyProjects]: {
    companyRoles: ["COMPANY_EXECUTIVE"]
  },
  [RequiredRoleCompanyLevelName.canManageSceneLicenses]: {
    companyRoles: ["COMPANY_EXECUTIVE"]
  },
  [RequiredRoleCompanyLevelName.canManageXgSeats]: {
    companyRoles: ["COMPANY_EXECUTIVE"]
  },
  [RequiredRoleCompanyLevelName.canViewMemberSeats]: {
    companyRoles: ["COMPANY_EXECUTIVE", "COMPANY_VIEWER"]
  }
};

/**
 * Checks whether a user has at least one of the required company roles.
 * !: Only use this method internally
 * To check for user access control better use hasUserValidRoleCompanyLevel.
 * In some cases the user might not have the required company role but could still
 * have access because a different role within the group.
 *
 * @param currentUser The user to get its company role from.
 * @param requiredCompanyRoles Array filled with company roles
 * that the user should have one of them
 * @returns True if the user has one of the required company roles.
 */
export function hasUserValidCompanyRole({
  currentUser,
  requiredCompanyRoles
}) {
  if (!currentUser) {
    // If there's no current user we assume that the user
    // is not logged in and therefore should not have access.
    return false;
  }
  if (!requiredCompanyRoles.length) {
    // If the requiredCompanyRoles were not provided or the
    // array was empty, we assume the user has a valid role.
    return true;
  }
  return requiredCompanyRoles.includes(currentUser.role);
}

/**
 * Checks whether a user has permissions within the company level.
 *
 * @returns True if the user has a valid company level role or permission.
 */
export function hasUserValidRoleCompanyLevel({
  roleName,
  currentUser,
  defaultRequiredRolesCompanyLevel = requiredRolesCompanyLevel,
  memberId,
  companyContext
}) {
  if (!roleName) {
    return true;
  }

  // Check first if user has enabled company level subscription roles
  // Since the subscription access is a required condition the roles should be checked first
  // Early exit with false here if the subscription is not available
  if (defaultRequiredRolesCompanyLevel[roleName].companySubscriptionRoles && companyContext && !hasCompanySubscriptionRoles({
    companyContext,
    requiredCompanySubscriptionRole: defaultRequiredRolesCompanyLevel[roleName].companySubscriptionRoles
  })) {
    return false;
  }
  const requiredCompanyRoles = defaultRequiredRolesCompanyLevel[roleName].companyRoles;
  if (!requiredCompanyRoles) {
    return true;
  }

  // Check if the user has enabled the permission to create groups.
  if (defaultRequiredRolesCompanyLevel[roleName].companyManager?.shouldCreateGroups && hasCreateGroupsPermission({
    member: currentUser
  })) {
    return true;
  }

  // When the user has access to something because he is performing an action on himself,
  // like accessing his own member profile page or changing his own password.
  if (defaultRequiredRolesCompanyLevel[roleName].shouldAllowCurrentUser && memberId && currentUser?.id && memberId === currentUser.id) {
    return true;
  }
  return hasUserValidCompanyRole({
    currentUser,
    requiredCompanyRoles
  });
}

/**
 * Checks whether the user has required company subscription for the user.
 * !: Only use this method internally
 * To check for user subscription role better use hasUserValidRoleCompanyLevel.
 *
 * @param companyContext The context of the selected company.
 * @param requiredCompanySubscriptionRole Array filled with required User subscription roles
 * that the user should have one of them
 * @returns True if the user has one of the required company subscription.
 */
export function hasCompanySubscriptionRoles({
  companyContext,
  requiredCompanySubscriptionRole
}) {
  /**
   * Early return if there is no required company subscription
   * In this case there is no required subscriptions
   */
  if (!requiredCompanySubscriptionRole?.length) {
    return true;
  }

  /**
   * Early return if no user roles in company context
   * In this case required subscription is not available
   * as the userRoles in companyContext is empty
   */
  if (!companyContext.userRoles.length) {
    return false;
  }

  // Return true if all required subscription role is available
  return requiredCompanySubscriptionRole.every(requiredSubscriptionRole => companyContext.userRoles.includes(requiredSubscriptionRole));
}